import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import { SpaceSearchAutocomplete } from "./SpaceSearchAutocomplete";

export type EditUsersDialogSpace = {
  mrn: string;
  name: string;
  assetCount: number;
};

export type EditUsersDialogProps = {
  open: boolean;
  organizationRole: { mrn: string; title: string };
  orgMrn: string;
  spaces: EditUsersDialogSpace[];
  onClose: () => void;
  onSave: (data: { removedSpaces: string[] }) => void;
  onEditRole: () => void;
  onAddSpace: (space: { mrn: string; name: string }) => void;
  isLoading?: boolean;
};

export function EditUsersDialog({
  open,
  organizationRole,
  orgMrn,
  spaces,
  onClose,
  onSave,
  onEditRole,
  onAddSpace,
  isLoading = false,
}: EditUsersDialogProps) {
  const [removedSpaces, setRemovedSpaces] = useState<Set<string>>(new Set());

  const handleReset = () => {
    setRemovedSpaces(new Set());
  };

  const handleRemoveSpace = (spaceMrn: string) => {
    setRemovedSpaces((prev) => new Set(prev).add(spaceMrn));
  };

  const handleSave = async () => {
    onSave({ removedSpaces: Array.from(removedSpaces) });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Filter out removed spaces
  const filteredSpaces = spaces.filter(
    (space) => !removedSpaces.has(space.mrn),
  );

  // Get MRNs of spaces the user currently has access to (excluding removed ones)
  const existingSpaceMrns = filteredSpaces.map((space) => space.mrn);

  const hasChanges = removedSpaces.size > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.default",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit users
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Organizational role section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 0.5, fontSize: "1rem" }}
          >
            Organizational role
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permissions are additive. A user will inherit the highest level
            permissions granted to them either on their individual profile, or
            through membership in a team or space.{" "}
            <Typography
              component="a"
              href="#"
              variant="body2"
              sx={{ color: "primary.main", textDecoration: "none" }}
            >
              Learn more
            </Typography>
          </Typography>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {organizationRole.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Default space permissions
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={onEditRole}
              sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
            >
              Edit
            </Button>
          </Box>
        </Box>

        {/* Spaces section */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 0.5, fontSize: "1rem" }}
          >
            Spaces
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Control the inventory these users can access
          </Typography>

          {/* Search to add a space */}
          <Box sx={{ mb: 2 }}>
            <SpaceSearchAutocomplete
              orgMrn={orgMrn}
              existingSpaceMrns={existingSpaceMrns}
              onSelectSpace={onAddSpace}
              disabled={isLoading}
            />
          </Box>

          {/* Spaces list */}
          <List sx={{ maxHeight: 300, overflow: "auto", p: 0 }}>
            {filteredSpaces.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                No spaces available
              </Typography>
            ) : (
              filteredSpaces.map((space) => (
                <ListItem
                  key={space.mrn}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {space.name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSpace(space.mrn)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges || isLoading}
            sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
          >
            Reset Changes
          </Button>
          <LoadingButton
            loading={isLoading}
            buttonText="Done"
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
            sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
